import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarController, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { trainingApi } from '../services/api';

ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, Title, Tooltip, Legend);

function Dashboard() {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  const [dashboardData, setDashboardData] = useState(null);
  const [pmDashboards, setPmDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [certificateEligibility, setCertificateEligibility] = useState(null);
  const [downloadingCertificate, setDownloadingCertificate] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await trainingApi.getDashboard();
      
      if (response.success) {
        setDashboardData(response.data);
        
        // Fetch detailed PM dashboards with quiz scores
        if (response.data.pms && Array.isArray(response.data.pms)) {
          const pmDetails = await Promise.all(
            response.data.pms.map(pm => {
              const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
              return fetch(`${apiUrl}/training/dashboard/pm/${pm.pm_id}`)
                .then(r => r.json())
                .then(d => d.data)
                .catch(e => ({ stats: pm, progress_records: [] }))
            })
          );
          setPmDashboards(pmDetails);
        }
        
        setLastUpdate(new Date());
      } else {
        throw new Error('Failed to fetch dashboard data');
      }
    } catch (err) {
      setError(err.message || 'Failed to load dashboard data');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/learning/recommendations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setRecommendations(data.recommendations);
      }
    } catch (err) {
      console.error('Error fetching recommendations:', err);
    }
  };

  const checkCertificateEligibility = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const tracks = ['CAPM', 'PMP'];
      
      const eligibilityPromises = tracks.map(track =>
        fetch(`${apiUrl}/learning/certificate/eligibility/${track}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        .then(r => r.json())
        .then(d => ({ track, ...d }))
      );
      
      const results = await Promise.all(eligibilityPromises);
      const eligible = results.find(r => r.eligible);
      setCertificateEligibility(eligible || null);
    } catch (err) {
      console.error('Error checking certificate eligibility:', err);
    }
  };

  const handleDownloadCertificate = async () => {
    if (!certificateEligibility) return;
    
    try {
      setDownloadingCertificate(true);
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const response = await fetch(
        `${apiUrl}/learning/certificate/generate/${certificateEligibility.track}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${certificateEligibility.track}_Certificate.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to generate certificate');
      }
    } catch (err) {
      console.error('Error downloading certificate:', err);
      alert('Failed to download certificate. Please try again.');
    } finally {
      setDownloadingCertificate(false);
    }
  };

  const handleDownloadProgressReport = async () => {
    try {
      setDownloadingReport(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/learning/report/progress`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Training_Progress_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Error downloading report:', err);
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloadingReport(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchRecommendations();
    checkCertificateEligibility();
    const interval = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto">
        <div className="text-red-600 text-center">
          <svg className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-xl font-bold mb-2">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Training Dashboard</h1>
            <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">Overview of certification progress and module completion</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          {/* Progress Report Button (LEARN-9) */}
          <button
            onClick={handleDownloadProgressReport}
            disabled={downloadingReport}
            className="bg-indigo-600 text-white px-3 py-2 text-sm rounded hover:bg-indigo-700 disabled:bg-gray-400 flex items-center space-x-2"
            title="Download your training progress report"
          >
            {downloadingReport ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">Generating...</span>
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">📊 Report</span>
              </>
            )}
          </button>

          {/* Certificate Button (LEARN-8) - Only show if eligible */}
          {certificateEligibility && (
            <button
              onClick={handleDownloadCertificate}
              disabled={downloadingCertificate}
              className="bg-green-600 text-white px-3 py-2 text-sm rounded hover:bg-green-700 disabled:bg-gray-400 flex items-center space-x-2"
              title={`Download your ${certificateEligibility.track} certificate`}
            >
              {downloadingCertificate ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="hidden sm:inline">Generating...</span>
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  <span className="hidden sm:inline">🎓 {certificateEligibility.track}</span>
                </>
              )}
            </button>
          )}

          <button
            onClick={fetchDashboardData}
            disabled={loading}
            className="bg-blue-600 text-white px-3 py-2 text-sm rounded hover:bg-blue-700 disabled:bg-gray-400 flex items-center space-x-2"
          >
            <svg className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
        {lastUpdate && (
          <p className="text-xs text-gray-500 mt-2 sm:text-right">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </p>
        )}
        </div>
      </div>

      {/* Stats Cards */}
      {dashboardData?.stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active PMs</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.stats.active_pms}</p>
              </div>
              <div className="bg-blue-100 rounded-full p-3">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.stats.in_progress}</p>
              </div>
              <div className="bg-yellow-100 rounded-full p-3">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.stats.completed}</p>
              </div>
              <div className="bg-green-100 rounded-full p-3">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overdue</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.stats.overdue}</p>
              </div>
              <div className="bg-red-100 rounded-full p-3">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recommended Modules (LEARN-7) */}
      {recommendations.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">💡 Recommended for You</h2>
          <p className="text-sm text-gray-600 mb-4">Based on your certification track and progress</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendations.slice(0, 3).map(module => (
              <Link
                key={module.id}
                to={`/module/${module.module_code}`}
                className="bg-white rounded-lg p-4 hover:shadow-lg transition-all border-2 border-transparent hover:border-blue-400"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="font-mono text-sm text-blue-600 font-semibold">
                    {module.module_code}
                  </span>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {module.certification_track || 'Both'}
                  </span>
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{module.module_name}</h3>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>📝 {module.question_count || 0} questions</span>
                  {module.avg_score && (
                    <span className="text-green-600">Avg: {module.avg_score.toFixed(0)}%</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
          {recommendations.length > 3 && (
            <div className="text-center mt-4">
              <Link 
                to="/modules"
                className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
              >
                View all {recommendations.length} recommendations →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Quiz Performance Chart */}
      {dashboardData?.pms && pmDashboards.length > 0 && (
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">📊 Quiz Performance Overview</h2>
            <p className="text-sm text-gray-600 mt-1">Average quiz scores by PM (80% required to complete modules)</p>
          </div>
          <div className="p-6">
            <Bar
              data={{
                labels: dashboardData.pms.map(pm => pm.pm_name),
                datasets: [
                  {
                    label: 'Quiz Score (%)',
                    data: pmDashboards.map(pd => pd?.stats?.overall_quiz_avg_score || 0),
                    backgroundColor: pmDashboards.map(pd => {
                      const score = pd?.stats?.overall_quiz_avg_score || 0;
                      return score >= 80 ? '#10b981' : score > 0 ? '#f59e0b' : '#d1d5db';
                    }),
                    borderColor: pmDashboards.map(pd => {
                      const score = pd?.stats?.overall_quiz_avg_score || 0;
                      return score >= 80 ? '#059669' : score > 0 ? '#d97706' : '#9ca3af';
                    }),
                    borderWidth: 1
                  }
                ]
              }}
              options={{
                indexAxis: 'y',
                responsive: true,
                plugins: {
                  legend: {
                    display: true,
                    position: 'top'
                  }
                },
                scales: {
                  x: {
                    min: 0,
                    max: 100,
                    ticks: {
                      callback: function(value) {
                        return value + '%';
                      }
                    }
                  }
                }
              }}
              height={300}
            />
          </div>
        </div>
      )}

      {/* PM List */}
      {dashboardData?.pms && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Project Managers</h2>
            <p className="text-sm text-gray-600 mt-1">Training progress, quiz scores, and module completion</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Certification</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modules</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quiz Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dashboardData.pms.map((pm, idx) => {
                  const pmDetail = pmDashboards[idx];
                  const quizScore = pmDetail?.stats?.overall_quiz_avg_score || 0;
                  
                  return (
                    <tr key={pm.pm_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-medium">
                              {pm.pm_name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{pm.pm_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{pm.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          pm.certification_target === 'PMP' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {pm.certification_target || 'None'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {pm.completed_modules} / {pm.total_modules}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-1">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${pm.avg_percent_complete || 0}%` }} />
                            </div>
                          </div>
                          <span className="ml-2 text-sm text-gray-600">{pm.avg_percent_complete || 0}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div className="flex-1">
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${quizScore >= 80 ? 'bg-green-500' : 'bg-yellow-500'}`}
                                style={{ width: `${Math.min(quizScore, 100)}%` }}
                              />
                            </div>
                          </div>
                          <span className={`ml-2 text-sm font-semibold ${quizScore >= 80 ? 'text-green-600' : 'text-yellow-600'}`}>
                            {quizScore.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {pm.overdue_count > 0 ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            {pm.overdue_count} Overdue
                          </span>
                        ) : pm.next_due_date ? (
                          <span className="text-sm text-gray-600">Due: {new Date(pm.next_due_date).toLocaleDateString()}</span>
                        ) : (
                          <span className="text-sm text-gray-400">No upcoming</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Link to={`/progress?pm=${pm.pm_id}`} className="text-blue-600 hover:text-blue-900">
                          View Details →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
